import React, { useMemo } from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import LabourAdvanceCard from './LabourAdvanceCard';
import PaymentModeBadge from '../shared/PaymentModeBadge';
import {
  AlertCircle,
  Banknote,
  Calendar,
  CheckCircle2,
  Filter,
  Search,
  Trash2,
  Wallet,
} from 'lucide-react';
import AppSelect from '../shared/AppSelect';

const filterSelectTrigger =
  'rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 text-left min-h-[44px] flex items-center justify-between';

function getPersonLabel(item, employees) {
  if (item.personType === 'Employee') {
    return employees.find((e) => e.employeeId === item.employeeId)?.name || item.employeeId || '—';
  }
  return item.personName || '—';
}

function monthKeyFromDate(dateStr) {
  if (!dateStr) return 'unknown';
  return dateStr.slice(0, 7);
}

function formatMonthLabel(monthStr) {
  if (!monthStr || monthStr === 'unknown') return 'Unknown period';
  const d = new Date(`${monthStr}-01`);
  if (Number.isNaN(d.getTime())) return monthStr;
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function formatPayDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function SettlementBadge({ settled }) {
  if (settled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-0.5 text-[11px] font-semibold text-teal-800 ring-1 ring-inset ring-teal-200">
        <CheckCircle2 className="h-3 w-3" aria-hidden />
        Settled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-semibold text-red-800 ring-1 ring-inset ring-red-200">
      <AlertCircle className="h-3 w-3" aria-hidden />
      Unsettled
    </span>
  );
}

export default function LabourAdvanceHistory({
  rows,
  employees,
  companyCurrency,
  labourAdvanceFilter,
  setLabourAdvanceFilter,
  labourAdvanceSort,
  setLabourAdvanceSort,
  onDelete,
  onSettleFromWallet,
  expandedCards,
  toggleCard,
}) {
  const fmt = (n) => formatCurrency(Number(n) || 0, companyCurrency);

  const stats = useMemo(() => {
    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const unsettledRows = rows.filter((r) => !r.settled);
    const unsettledTotal = unsettledRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return {
      count: rows.length,
      unsettledCount: unsettledRows.length,
      total,
      unsettledTotal,
    };
  }, [rows]);

  const numberedEntries = useMemo(() => rows.map((entry, i) => ({ entry, rowNum: i + 1 })), [rows]);

  const groupedByMonth = useMemo(() => {
    const groups = [];
    let currentKey = null;
    numberedEntries.forEach(({ entry, rowNum }) => {
      const key = monthKeyFromDate(entry.date);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ monthKey: key, label: formatMonthLabel(key), entries: [] });
      }
      groups[groups.length - 1].entries.push({ entry, rowNum });
    });
    return groups;
  }, [numberedEntries]);

  const hasActiveFilters =
    labourAdvanceFilter.search ||
    labourAdvanceFilter.personType ||
    labourAdvanceFilter.paymentType ||
    labourAdvanceFilter.settlementStatus;

  const sortValue = `${labourAdvanceSort.field}_${labourAdvanceSort.order}`;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-md overflow-hidden ring-1 ring-black/5">
      <div className="bg-gradient-to-r from-slate-700 via-slate-700 to-teal-800 px-5 py-5 text-white">
        <h3 className="text-lg font-semibold tracking-tight">Entry history</h3>
        <p className="text-slate-300 text-sm mt-1">
          Labour payments & advances · {rows.length} record{rows.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gradient-to-b from-slate-50/80 to-white border-b border-gray-100">
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-teal-700 mb-1">
            <Banknote className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Entries</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{stats.count}</p>
        </div>
        <div className="rounded-xl border border-red-100 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-red-500 mb-1">
            <AlertCircle className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Unsettled</span>
          </div>
          <p className="text-xl font-bold text-red-700">{stats.unsettledCount}</p>
        </div>
        <div className="rounded-xl border border-teal-100 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-teal-600 mb-1">
            <Banknote className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Total paid</span>
          </div>
          <p className="text-lg font-bold text-teal-800 truncate">{fmt(stats.total)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 shadow-sm">
          <div className="flex items-center gap-2 text-teal-800 mb-1">
            <Wallet className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Outstanding</span>
          </div>
          <p className="text-lg font-bold text-slate-800 truncate">{fmt(stats.unsettledTotal)}</p>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-gray-100 bg-white space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <Filter className="h-4 w-4 text-teal-600" />
          Filters & sort
        </div>
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search by person name…"
              value={labourAdvanceFilter.search}
              onChange={(e) => setLabourAdvanceFilter((f) => ({ ...f, search: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:border-teal-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-teal-600"
            />
          </div>
          <AppSelect
            value={labourAdvanceFilter.personType}
            onChange={(e) => setLabourAdvanceFilter((f) => ({ ...f, personType: e.target.value }))}
            aria-label="Person type"
            sheetTitle="Person type"
            placeholder="All person types"
            options={[
              { value: '', label: 'All person types' },
              { value: 'Employee', label: 'Employee' },
              { value: 'Labour', label: 'Labour' },
              { value: 'Freelancer', label: 'Freelancer' },
              { value: 'Other', label: 'Other' },
            ]}
            triggerClassName={filterSelectTrigger}
          />
          <AppSelect
            value={labourAdvanceFilter.paymentType}
            onChange={(e) => setLabourAdvanceFilter((f) => ({ ...f, paymentType: e.target.value }))}
            aria-label="Payment type"
            sheetTitle="Payment type"
            placeholder="All payment types"
            options={[
              { value: '', label: 'All payment types' },
              { value: 'Advance', label: 'Advance' },
              { value: 'Festival Bonus', label: 'Festival Bonus' },
              { value: 'Bonus', label: 'Bonus' },
              { value: 'Incentive', label: 'Incentive' },
              { value: 'Other', label: 'Other' },
            ]}
            triggerClassName={filterSelectTrigger}
          />
          <AppSelect
            value={labourAdvanceFilter.settlementStatus}
            onChange={(e) => setLabourAdvanceFilter((f) => ({ ...f, settlementStatus: e.target.value }))}
            aria-label="Settlement status"
            sheetTitle="Settlement status"
            placeholder="All statuses"
            options={[
              { value: '', label: 'All statuses' },
              { value: 'settled', label: 'Settled' },
              { value: 'unsettled', label: 'Unsettled' },
            ]}
            triggerClassName={filterSelectTrigger}
          />
          <AppSelect
            value={sortValue}
            onChange={(e) => {
              const [field, order] = e.target.value.split('_');
              setLabourAdvanceSort({ field, order });
            }}
            aria-label="Sort"
            sheetTitle="Sort"
            options={[
              { value: 'createdAt_desc', label: 'Newest entry first' },
              { value: 'createdAt_asc', label: 'Oldest entry first' },
              { value: 'date_desc', label: 'Date (newest first)' },
              { value: 'date_asc', label: 'Date (oldest first)' },
              { value: 'amount_desc', label: 'Amount (high to low)' },
              { value: 'amount_asc', label: 'Amount (low to high)' },
              { value: 'person_asc', label: 'Person (A–Z)' },
              { value: 'person_desc', label: 'Person (Z–A)' },
            ]}
            triggerClassName={`${filterSelectTrigger} lg:min-w-[180px]`}
          />
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() =>
                setLabourAdvanceFilter({
                  search: '',
                  personType: '',
                  paymentType: '',
                  settlementStatus: '',
                })
              }
              className="text-sm font-medium text-teal-700 hover:text-teal-900 whitespace-nowrap px-2"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        {rows.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mb-3">
              <Banknote className="h-7 w-7 text-teal-500" />
            </div>
            <p className="text-gray-500 font-medium">No entries found</p>
            <p className="text-sm text-gray-400 mt-1">
              {hasActiveFilters ? 'Try adjusting filters' : 'Add your first labour or advance entry'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white text-left text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-3 w-12 text-center">#</th>
                  <th className="px-4 py-3">Person</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3">Settlement</th>
                  <th className="px-4 py-3 text-center w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedByMonth.map((group) => (
                  <React.Fragment key={group.monthKey}>
                    <tr className="bg-slate-100/90">
                      <td colSpan={9} className="px-4 py-2.5">
                        <div className="flex items-center gap-2 text-slate-800 font-semibold text-xs uppercase tracking-wide">
                          <Calendar className="h-4 w-4" />
                          {group.label}
                          <span className="font-normal text-teal-800 normal-case">
                            ({group.entries.length} entr{group.entries.length !== 1 ? 'ies' : 'y'})
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.entries.map(({ entry, rowNum }) => {
                      const personLabel = getPersonLabel(entry, employees);
                      const zebra = rowNum % 2 === 0;
                      return (
                        <tr
                          key={entry.id}
                          className={`border-b border-gray-100 transition-colors hover:bg-teal-50/40 ${
                            zebra ? 'bg-white' : 'bg-slate-50/60'
                          }`}
                        >
                          <td className="px-4 py-3 text-center text-xs font-mono text-gray-400">{rowNum}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-teal-600 to-teal-700 text-white flex items-center justify-center text-sm font-bold shadow-sm shrink-0">
                                {personLabel.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{personLabel}</p>
                                <p className="text-xs text-gray-500">{entry.personType}</p>
                                {entry.settledFromOtherWallet?.amount > 0 && (
                                  <p className="text-[10px] text-teal-700 flex items-center gap-0.5 mt-1">
                                    <Wallet className="h-3 w-3" />
                                    {entry.settledFromOtherWallet.sourceEmployeeName}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                            {formatPayDate(entry.date)}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{entry.paymentType}</td>
                          <td className="px-4 py-3">
                            <PaymentModeBadge mode={entry.paymentMode} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-block rounded-lg bg-teal-100 px-2.5 py-1 font-bold text-slate-800 tabular-nums">
                              {fmt(entry.amount)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <SettlementBadge settled={entry.settled} />
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 max-w-[200px]">
                            {entry.settled ? (
                              <div>
                                <div className="font-medium text-teal-700">
                                  {entry.appliedInMonth || 'Settled'}
                                </div>
                                {entry.settlementRemark && (
                                  <div className="text-gray-500 truncate">{entry.settlementRemark}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-0.5">
                              {!entry.settled && onSettleFromWallet && (
                                <button
                                  type="button"
                                  onClick={() => onSettleFromWallet(entry)}
                                  className="p-2 rounded-lg text-teal-700 hover:bg-teal-50"
                                  title="Settle from wallet"
                                >
                                  <Wallet className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => onDelete(entry.id)}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold text-sm">
                  <td colSpan={5} className="px-4 py-3 text-right text-gray-700 uppercase text-xs tracking-wide">
                    Totals ({stats.count})
                  </td>
                  <td className="px-4 py-3 text-right text-slate-800 tabular-nums">{fmt(stats.total)}</td>
                  <td colSpan={3} className="px-4 py-3 text-right text-red-700 text-xs">
                    Outstanding: {fmt(stats.unsettledTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="lg:hidden p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {rows.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-sm">No entries</p>
        ) : (
          rows.map((item) => (
            <LabourAdvanceCard
              key={item.id}
              payment={item}
              isExpanded={expandedCards.has(item.id)}
              onToggle={toggleCard}
              onDelete={onDelete}
              onSettleFromWallet={onSettleFromWallet}
              employees={employees}
              companyCurrency={companyCurrency}
            />
          ))
        )}
      </div>
    </div>
  );
}
